'use client';

import { useSyncExternalStore } from 'react';
import { buildWhatsappHref, isAndroid, resolveWhatsappHref } from '@/lib/whatsapp';

/**
 * A plain anchor to WhatsApp that upgrades itself to a package-pinned Android
 * intent: URL after mount, so WhatsApp Business cannot claim the tap.
 *
 * Exists for SERVER components (not-found, contact, disabled), which cannot use
 * the WhatsAppProvider hook. Client components should keep using useWhatsApp().
 *
 * The first render is always the plain https href, matching the server HTML, so
 * there is no hydration mismatch and no intent: URL is ever baked into static
 * output served to iOS or desktop.
 */
/** The user agent cannot change for the life of the document. */
function subscribeNoop(): () => void {
  return () => {};
}

export function WhatsAppAnchor({
  number,
  prefill,
  className,
  children,
}: {
  number: string;
  prefill?: string;
  className?: string;
  children: React.ReactNode;
}) {
  // Static external read, not state: false on the server so the first paint
  // matches the server HTML, true on Android once hydrated.
  const android = useSyncExternalStore(subscribeNoop, () => isAndroid(), () => false);
  const href = android
    ? resolveWhatsappHref(number, prefill, navigator.userAgent)
    : buildWhatsappHref(number, prefill);

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}
